import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { Text as PaperText, TextInput as PaperTextInput, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { captureException, errorToString } from '@/lib/errors'
import { notify } from '@/lib/notice'
import { pb } from '@/lib/pocketbase'
import { Button } from '@/ui/Button'
import { Link } from '@/ui/Link'

export default function ResetPasswordPage() {
    useAvoidSoftInputForScreen()
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState<string | null>(null)

    const mutation = useMutation({
        mutationFn: async (email: string) => {
            await pb.collection('users').requestPasswordReset(email)
        },
        onSuccess: () => {
            notify.success('Check your email for password reset instructions.')
        },
        onError: error => {
            captureException('Password reset request', error)
            const errorMessage = errorToString(error, 'Failed to send password reset')

            notify.error(errorMessage)
        },
    })

    const handleSubmit = () => {
        setEmailError(null)

        if (!email.trim()) {
            setEmailError('Email is required')
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address')
            return
        }

        mutation.mutate(email)
    }

    const handleTryAgain = () => {
        mutation.reset()
        setEmail('')
        setEmailError(null)
    }

    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    if (mutation.isSuccess) {
        return (
            <ContentWrapper
                style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
            >
                <View style={styles.container}>
                    <View style={styles.content}>
                        <PaperText variant="headlineLarge">Check Your Email</PaperText>
                        <PaperText style={styles.description}>
                            We've sent a password reset link to your email address. Click the link
                            in the email to reset your password.
                        </PaperText>
                    </View>

                    <View style={styles.actions}>
                        <View style={styles.actionRow}>
                            <PaperText style={styles.description}>
                                Didn't receive the email?
                            </PaperText>
                            <Button title="Try Again" mode="outlined" onPress={handleTryAgain} />
                        </View>

                        <View style={styles.actionRow}>
                            <PaperText style={styles.description}>
                                Remember your password?
                            </PaperText>
                            <Link href="/auth/sign-in">
                                <PaperText style={{ color: theme.colors.primary }}>
                                    Sign In
                                </PaperText>
                            </Link>
                        </View>
                    </View>
                </View>
            </ContentWrapper>
        )
    }

    return (
        <ContentWrapper
            style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
        >
            <View style={styles.container}>
                <View style={styles.content}>
                    <PaperText variant="headlineLarge">Reset Password</PaperText>
                    <PaperText style={styles.description}>
                        Enter your email address and we'll send you a link to reset your password
                    </PaperText>
                </View>

                <View style={styles.form}>
                    <PaperTextInput
                        label="Email Address"
                        placeholder="Enter your email"
                        value={email}
                        onChangeText={text => {
                            setEmail(text)
                            if (emailError) setEmailError(null)
                        }}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        error={!!emailError}
                        disabled={mutation.isPending}
                    />

                    {emailError && (
                        <PaperText style={[styles.errorText, { color: semanticColors.errorText }]}>
                            {emailError}
                        </PaperText>
                    )}

                    <Button
                        title="Send Reset Email"
                        mode="contained"
                        onPress={handleSubmit}
                        disabled={mutation.isPending}
                        loading={mutation.isPending}
                        loadingTitle="Sending Reset Email..."
                    />
                </View>

                <View style={styles.actions}>
                    <View style={styles.actionRow}>
                        <PaperText style={styles.description}>Remember your password?</PaperText>
                        <Link href="/auth/sign-in">
                            <PaperText style={{ color: theme.colors.primary }}>Sign In</PaperText>
                        </Link>
                    </View>

                    <View style={styles.actionRow}>
                        <PaperText style={styles.description}>Don't have an account?</PaperText>
                        <Link href="/auth/sign-up">
                            <PaperText style={{ color: theme.colors.primary }}>Sign Up</PaperText>
                        </Link>
                    </View>
                </View>
            </View>
        </ContentWrapper>
    )
}

const styles = StyleSheet.create({
    safeContainer: {
        flex: 1,
    },
    container: {
        flex: 1,
        padding: 16,
        gap: 16,
        maxWidth: 400,
        width: '100%',
        alignSelf: 'center',
    },
    content: {
        gap: 8,
        marginBottom: 16,
    },
    description: {
        opacity: 0.7,
    },
    form: {
        gap: 16,
    },
    errorText: {
        fontSize: 14,
    },
    actions: {
        gap: 12,
        marginTop: 16,
    },
    actionRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    },
})
